import React from 'react';
import {useIntl} from 'react-intl';
import styled from 'styled-components';
import {Tooltip} from '@forecast-it/design-system';
import {taskSetColour} from './taskSetColours';
import {TaskSetRepeatIcon} from './TaskSetRepeatIcon';

/**
 * The same pill geometry the scoping chip and the drawer's name chip use, drawn
 * as an outline rather than a fill. The outline is the visible difference
 * between a chip that opens the set and one that only names it: this one is
 * read-only, because the task modal opens from pages where the Manage task sets
 * drawer does not belong (PIKA-789 AC4).
 *
 * Every element here is a div, and that is load-bearing. options_section.scss
 * nests the rule, so it compiles to
 * `.task-modal-v3-options-section .left-section span:not(.day)` -- three classes
 * and one element, (0,3,1) -- which reaches any span at any depth inside the
 * options row, gives it `display: flex` and a pointer cursor, and outweighs any
 * single styled-components class. That would cost this chip its ellipsis (AC9)
 * and make it look clickable (AC4). Divs are not matched.
 * Do not convert these to spans.
 */
const OutlineChip = styled.div<{foreground: string}>`
	display: inline-flex;
	align-items: center;
	gap: 4px;
	max-width: 220px;
	height: 19px;
	/* 1px tighter than the filled chip's 8/6, so the border lands inside the same box. */
	padding: 0 7px 0 5px;
	border: 1px solid ${({foreground}) => foreground};
	border-radius: 9.5px;
	background: transparent;
	color: ${({foreground}) => foreground};
	font-size: 11px;
	font-weight: 600;
	min-width: 0;
	cursor: default;
`;

/** Truncates rather than widening the options row; the full name is on the
    tooltip and in the chip's accessible name. */
const ChipName = styled.div`
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
	min-width: 0;
`;

const ChipPosition = styled.div`
	white-space: nowrap;
	flex-shrink: 0;
`;

export interface TaskSetModalChipProps {
	readonly taskSetId: string;
	readonly name: string | null;
	readonly sequence: number | null;
	readonly total: number | null;
	readonly showPosition: boolean;
}

export const TaskSetModalChip = ({taskSetId, name, sequence, total, showPosition}: TaskSetModalChipProps) => {
	const intl = useIntl();
	const colour = taskSetColour(taskSetId);

	const position = showPosition ? intl.formatMessage({id: 'task_set.instance_sequence'}, {sequence, total}) : null;
	const label = intl.formatMessage({id: 'task_set.modal_label'});
	const whereToChange = intl.formatMessage({id: 'task_set.modal_where_to_change'});

	// Repeats what the chip shows, untruncated, so a screen reader gets the whole
	// set name and the position without needing the colour (AC9, AC12).
	// A nameless set whose position cannot be trusted leaves nothing to append, so
	// the label stands alone rather than trailing a colon a screen reader announces.
	const named = [name, position].filter(part => part != null && part !== '').join(', ');
	const accessibleName = named ? `${label}: ${named}` : label;

	// Two facts, two lines: Tooltip renders its content into a single <Text>,
	// where a "\n" would collapse and the two would run together. Tooltip.Item is
	// what this repo's other two-part callers pass.
	//
	// `interactable` is left at its default (true), deliberately. With
	// `interactable={false}` the design system wraps the chip in its own
	// `tabIndex={0}` <TriggerContent> div, which carries no role and no
	// accessible name -- so the element a keyboard user lands on is nameless and
	// the role/aria-label below sit on a child that never receives focus. At the
	// default, Radix composes the trigger onto OutlineChip itself via `asChild`
	// (styled.div forwards the ref), making the chip the focus target, the
	// tooltip anchor and the named element at once. The click the other path
	// cancels is not missed: a div with no handler does nothing on click.
	return (
		<Tooltip content={<Tooltip.Item title={name ?? undefined} details={whereToChange} />}>
			<OutlineChip
				foreground={colour.foreground}
				tabIndex={0}
				role="note"
				aria-label={accessibleName}
				data-cy="task-set-modal-chip"
			>
				<TaskSetRepeatIcon />
				{name ? <ChipName>{name}</ChipName> : null}
				{position ? <ChipPosition>· {position}</ChipPosition> : null}
			</OutlineChip>
		</Tooltip>
	);
};
